import React, { useCallback, useEffect, useState } from 'react';
import {
  APPLICATION_STATUSES,
  DECISIONS_NEEDING_A_NOTE,
  createCall,
  decideApplication,
  listApplications,
  listCalls,
  updateCall,
  type CastingApplication,
  type CastingCall,
  type CastingCallInput,
} from '../../../services/castingAdminService';

/**
 * Creating casting calls and reviewing the applications they receive.
 *
 * The endpoints behind this page have existed for some time with no screen, which is why
 * production has no casting calls at all: there was no way to make one short of calling
 * the API by hand. This is the screen for the flow described for the platform - somebody
 * applies, an admin reviews, a decision goes out with a reason.
 *
 * The note is required in the UI for the two decisions that end an applicant's
 * involvement, and the server requires it too. The duplication is deliberate: the client
 * check exists so a reviewer is told before they lose what they typed, and the server
 * check is the one that actually holds.
 */

const CALL_TYPES = [
  { value: 'CAST', label: 'Cast' },
  { value: 'CREW', label: 'Crew' },
  { value: 'COLLABORATOR', label: 'Collaborator' },
];

const CALL_STATUSES = [
  { value: 'DRAFT', label: 'Draft - not visible to anyone' },
  { value: 'OPEN', label: 'Open - accepting applications' },
  { value: 'CLOSED', label: 'Closed - visible, not accepting' },
  { value: 'FILLED', label: 'Filled' },
];

const emptyCall: CastingCallInput = {
  title: '', callType: 'CAST', roleName: '', roleSummary: '', description: '',
  requirements: '', location: '', ageRange: '', remoteFriendly: false, paid: false,
  compensationSummary: '', shootDatesSummary: '', submissionDeadline: '', status: 'DRAFT',
};

const CastingManagement: React.FC = () => {
  const [calls, setCalls] = useState<CastingCall[]>([]);
  const [selected, setSelected] = useState<CastingCall | null>(null);
  const [applications, setApplications] = useState<CastingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<CastingCall | null>(null);
  const [form, setForm] = useState<CastingCallInput>(emptyCall);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const refreshCalls = useCallback(async () => {
    setLoading(true);
    try {
      setCalls(await listCalls());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load casting calls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refreshCalls(); }, [refreshCalls]);

  const openCall = async (call: CastingCall) => {
    setSelected(call);
    setApplications([]);
    try {
      setApplications(await listApplications(call.slug));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load applications');
    }
  };

  const startNew = () => { setEditing(null); setForm(emptyCall); setShowForm(true); };

  const startEdit = (call: CastingCall) => {
    setEditing(call);
    setForm({
      title: call.title, callType: call.callType, roleName: call.roleName ?? '',
      roleSummary: call.roleSummary ?? '', location: call.location ?? '',
      ageRange: call.ageRange ?? '', remoteFriendly: call.remoteFriendly, paid: call.paid,
      compensationSummary: call.compensationSummary ?? '',
      shootDatesSummary: call.shootDatesSummary ?? '',
      submissionDeadline: call.submissionDeadline ?? '', status: call.status,
    });
    setShowForm(true);
  };

  const submitCall = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('A title is required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateCall(editing.slug, form);
        setNotice('Casting call updated.');
      } else {
        await createCall(form);
        setNotice('Casting call created.');
      }
      setShowForm(false);
      setError(null);
      await refreshCalls();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the casting call');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Casting</h1>
            <p className="text-sm text-gray-600">Create calls, and review the applications they receive.</p>
          </div>
          <button
            type="button"
            onClick={startNew}
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500"
          >
            New casting call
          </button>
        </div>

        {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        {notice && <p className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</p>}

        <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
          <section aria-label="Casting calls" className="rounded-xl border border-gray-200">
            <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">
              Calls {calls.length > 0 && <span className="text-gray-500">({calls.length})</span>}
            </h2>
            {loading ? (
              <p className="px-4 py-6 text-sm text-gray-600">Loading…</p>
            ) : calls.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-600">
                No casting calls yet. Create one and it appears on the public casting page once its status is Open.
              </p>
            ) : (
              <ul>
                {calls.map((call) => (
                  <li key={call.id} className="border-b border-gray-100 last:border-0">
                    <div className="flex items-start justify-between gap-2 px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openCall(call)}
                        className={`text-left ${selected?.id === call.id ? 'text-orange-700' : 'text-gray-900'}`}
                      >
                        <span className="block text-sm font-medium">{call.title}</span>
                        <span className="block text-xs text-gray-600">
                          {call.callTypeLabel ?? call.callType} · {call.status}
                          {call.roleName ? ` · ${call.roleName}` : ''}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => startEdit(call)}
                        className="text-xs font-medium text-orange-700 underline"
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Applications" className="rounded-xl border border-gray-200">
            <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">
              {selected ? `Applications for ${selected.title}` : 'Applications'}
            </h2>
            {!selected ? (
              <p className="px-4 py-6 text-sm text-gray-600">Choose a casting call to see its applications.</p>
            ) : applications.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-600">No applications for this call yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {applications.map((application) => (
                  <ApplicationRow
                    key={application.id}
                    application={application}
                    onDecided={(updated) => {
                      setApplications((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
                      setNotice(`${updated.applicantName} marked ${updated.statusLabel ?? updated.status}.`);
                    }}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={submitCall}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6"
          >
            <h2 className="mb-4 text-lg font-bold text-gray-900">
              {editing ? 'Edit casting call' : 'New casting call'}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Title *" className="sm:col-span-2">
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Type">
                <select value={form.callType} onChange={(e) => setForm({ ...form, callType: e.target.value })} className={inputClass}>
                  {CALL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputClass}>
                  {CALL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Role name">
                <input value={form.roleName} onChange={(e) => setForm({ ...form, roleName: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Age range">
                <input value={form.ageRange} onChange={(e) => setForm({ ...form, ageRange: e.target.value })} placeholder="e.g. 25-35" className={inputClass} />
              </Field>
              <Field label="Role summary" className="sm:col-span-2">
                <input value={form.roleSummary} onChange={(e) => setForm({ ...form, roleSummary: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Description" className="sm:col-span-2">
                <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Requirements" className="sm:col-span-2">
                <textarea rows={3} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Location">
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Shoot dates">
                <input value={form.shootDatesSummary} onChange={(e) => setForm({ ...form, shootDatesSummary: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Compensation">
                <input value={form.compensationSummary} onChange={(e) => setForm({ ...form, compensationSummary: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Submission deadline">
                <input type="date" value={(form.submissionDeadline ?? '').slice(0, 10)} onChange={(e) => setForm({ ...form, submissionDeadline: e.target.value })} className={inputClass} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} />
                Paid
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input type="checkbox" checked={!!form.remoteFriendly} onChange={(e) => setForm({ ...form, remoteFriendly: e.target.checked })} />
                Remote friendly
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-60">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create call'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500';

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({ label, className, children }) => (
  <div className={className}>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    {children}
  </div>
);

/** One application, with the review controls. */
const ApplicationRow: React.FC<{
  application: CastingApplication;
  onDecided: (updated: CastingApplication) => void;
}> = ({ application, onDecided }) => {
  const [status, setStatus] = useState(application.status);
  const [note, setNote] = useState(application.studioNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const noteRequired = DECISIONS_NEEDING_A_NOTE.includes(status);
  const chosen = APPLICATION_STATUSES.find((s) => s.value === status);

  const submit = async () => {
    if (noteRequired && !note.trim()) {
      // Caught here so the reviewer is told before they lose what they typed. The server
      // enforces it too, and that is the check that actually holds.
      setProblem('A note is required for this decision. It is sent to the applicant.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      onDecided(await decideApplication(application.id, status, note.trim()));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Could not save the decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="px-4 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">{application.applicantName}</p>
        <p className="text-xs text-gray-600">
          {application.statusLabel ?? application.status}
          {application.submittedAt ? ` · applied ${application.submittedAt.slice(0, 10)}` : ''}
        </p>
      </div>
      <p className="text-xs text-gray-600">
        {application.applicantEmail}
        {application.location ? ` · ${application.location}` : ''}
        {application.memberCraftLabel ? ` · ${application.memberCraftLabel}` : ''}
      </p>

      {application.coverNote && <p className="mt-2 whitespace-pre-line text-sm text-gray-800">{application.coverNote}</p>}

      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        {application.selfTapeUrl && <a className="text-orange-700 underline" href={application.selfTapeUrl} target="_blank" rel="noreferrer">Self-tape</a>}
        {application.reelUrl && <a className="text-orange-700 underline" href={application.reelUrl} target="_blank" rel="noreferrer">Reel</a>}
        {application.portfolioUrl && <a className="text-orange-700 underline" href={application.portfolioUrl} target="_blank" rel="noreferrer">Portfolio</a>}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-[14rem_1fr_auto] sm:items-start">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={`Decision for ${application.applicantName}`}
          className={inputClass}
        >
          {APPLICATION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={`Note to ${application.applicantName}`}
            placeholder={noteRequired ? 'Required - this is sent to the applicant' : 'Optional note'}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-gray-600">{chosen?.help}</p>
        </div>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save decision'}
        </button>
      </div>

      {problem && <p role="alert" className="mt-2 text-sm text-red-700">{problem}</p>}
    </li>
  );
};

export default CastingManagement;
